/**
 * Configuration mapping procedure types to their allowed fields.
 * Based on analysis of 128 canonical rows from the SampleStatus/Violet Events exports.
 *
 * Field names map to database column names from the samples table.
 */

export type ProcedureTypeCategory =
  | 'Sampling - patient'
  | 'EEG monitoring'
  | 'Transfer / shipping'
  | 'Processing / maturation'
  | 'Other';

// Mapping from user-friendly field names to database column names
export const FIELD_NAME_MAPPING: Record<string, string> = {
  'Name': 'title',
  'Active?': 'is_active',
  'Banking entity': 'banking_entity_id',
  'Blocking': 'blocking',
  'Blocked by': 'blocked_by_sample_id', // Blocked by references another sample (separate from source_sample_id)
  'Date': 'event_start_date',
  'Location – destination / transfer to': 'location_destination_id',
  'Location – where performed': 'location_performed_id',
  'Next check-in date': 'next_check_in_date',
  'Outcome / notes': 'outcome_notes',
  'Patient': 'subject_id',
  'Performed on': 'performed_on',
  'Performing entity': 'performing_entity_id',
  'Priority': 'priority',
  'Procedure type': 'procedure_type',
  'Sample received happened': 'sample_received',
  'Status – patient': 'status_patient',
  'Status – sample / cells': 'status_sample',
  'Transfer destination': 'transfer_destination_id',
  'Transfer involved?': 'transfer_involved',
  'Transfer origin': 'transfer_origin_id',
  'Analysis completion': 'analysis_complete',
  'Source sample event': 'source_sample_id',
};

// Database column names for each procedure type
// These are the fields that should be visible/editable for each procedure type
export const PROCEDURE_TYPE_FIELDS: Record<ProcedureTypeCategory, string[]> = {
  'Sampling - patient': [
    'title',
    'is_active',
    'banking_entity_id',
    'blocking',
    'event_start_date',
    'location_destination_id',
    'location_performed_id',
    'next_check_in_date',
    'outcome_notes',
    'subject_id',
    'performed_on',
    'performing_entity_id',
    'priority',
    'procedure_type',
    'sample_received',
    'status_patient',
    'status_sample',
    'transfer_destination_id',
    'transfer_involved',
    'transfer_origin_id',
  ],

  'EEG monitoring': [
    'title',
    'is_active',
    'analysis_complete',
    'event_start_date',
    'location_performed_id',
    'next_check_in_date',
    'outcome_notes',
    'subject_id',
    'performed_on',
    'performing_entity_id',
    'priority',
    'procedure_type',
    'sample_received',
    'status_patient',
    'status_sample',
    'transfer_involved',
  ],

  'Transfer / shipping': [
    'title',
    'is_active',
    'banking_entity_id',
    'blocked_by_sample_id', // Blocked by (references another sample that blocks this one)
    'blocking',
    'event_start_date',
    'location_destination_id',
    'location_performed_id',
    'next_check_in_date',
    'outcome_notes',
    'subject_id',
    'performed_on',
    'performing_entity_id',
    'priority',
    'procedure_type',
    'sample_received',
    'source_sample_id', // Source sample event (the sample this event is derived from)
    'status_patient',
    'status_sample',
    'transfer_destination_id',
    'transfer_involved',
    'transfer_origin_id',
  ],

  'Processing / maturation': [
    'title',
    'is_active',
    'analysis_complete',
    'banking_entity_id',
    'blocked_by_sample_id', // Blocked by (references another sample that blocks this one)
    'blocking',
    'event_start_date',
    'location_performed_id',
    'next_check_in_date',
    'outcome_notes',
    'subject_id',
    'performed_on',
    'performing_entity_id',
    'priority',
    'procedure_type',
    'sample_received',
    'source_sample_id', // Source sample event (the sample this event is derived from)
    'status_sample',
    'transfer_involved',
  ],

  'Other': [
    'title',
    'is_active',
    'analysis_complete',
    'banking_entity_id',
    'blocked_by_sample_id', // Blocked by (references another sample that blocks this one)
    'blocking',
    'event_start_date',
    'location_destination_id',
    'location_performed_id',
    'next_check_in_date',
    'outcome_notes',
    'subject_id',
    'performed_on',
    'performing_entity_id',
    'priority',
    'procedure_type',
    'sample_received',
    'source_sample_id', // Source sample event (the sample this event is derived from)
    'status_sample',
    'transfer_involved',
  ],
};

// Get unique fields across all procedure types (for reference)
export const ALL_PROCEDURE_FIELDS = Array.from(
  new Set(Object.values(PROCEDURE_TYPE_FIELDS).flat())
);

// Helper to get the procedure type category from a procedure_type string value
export function getProcedureTypeCategory(procedureTypeValue: string | null | undefined): ProcedureTypeCategory {
  if (!procedureTypeValue) return 'Other';

  const lower = procedureTypeValue.toLowerCase();

  // Match sampling – patient
  if (lower.includes('sampling') ||
      lower.includes('blood draw') ||
      lower.includes('biopsy') ||
      lower.includes('skin punch')) {
    return 'Sampling - patient';
  }

  // Match EEG monitoring
  if (lower.includes('eeg')) {
    return 'EEG monitoring';
  }

  // Match Transfer / shipping
  if (lower.includes('transfer') || lower.includes('shipping')) {
    return 'Transfer / shipping';
  }

  // Match Processing / maturation
  if (lower.includes('processing') ||
      lower.includes('maturation') ||
      lower.includes('isolation') ||
      lower.includes('reprogramming') ||
      lower.includes('storage') ||
      lower.includes('pbmc')) {
    return 'Processing / maturation';
  }

  return 'Other';
}

// Get allowed fields for a given procedure type value
export function getFieldsForProcedureType(procedureTypeValue: string | null | undefined): string[] {
  const category = getProcedureTypeCategory(procedureTypeValue);
  return PROCEDURE_TYPE_FIELDS[category];
}

// Check if a field should be visible for a given procedure type
export function isFieldVisibleForProcedureType(
  fieldName: string,
  procedureTypeValue: string | null | undefined
): boolean {
  const allowedFields = getFieldsForProcedureType(procedureTypeValue);
  return allowedFields.includes(fieldName);
}

// Get all procedure type categories
export const PROCEDURE_TYPE_CATEGORIES: ProcedureTypeCategory[] = [
  'Sampling - patient',
  'EEG monitoring',
  'Transfer / shipping',
  'Processing / maturation',
  'Other',
];

// Get description for each procedure type category
export function getProcedureTypeDescription(category: ProcedureTypeCategory): string {
  const descriptions: Record<ProcedureTypeCategory, string> = {
    'Sampling - patient': 'Blood draws, biopsies, and other patient sample collection',
    'EEG monitoring': 'EEG cap placement, recording sessions, and monitoring',
    'Transfer / shipping': 'Sample transfers between labs and shipping events',
    'Processing / maturation': 'PBMC isolation, iPSC reprogramming, storage, and processing',
    'Other': 'Analysis, research, and other event types',
  };
  return descriptions[category];
}

// Map view names to their expected procedure type categories
// When creating from a view, auto-select the matching procedure type
export const VIEW_TO_PROCEDURE_TYPE: Record<string, ProcedureTypeCategory> = {
  'vw_outstanding_transfers': 'Transfer / shipping',
  'vw_sampling_patient': 'Sampling - patient', // Also includes EEG but defaults to Sampling
};

// Get the default procedure type for a view (if any)
export function getViewDefaultProcedureType(viewName: string | undefined): ProcedureTypeCategory | null {
  if (!viewName) return null;
  return VIEW_TO_PROCEDURE_TYPE[viewName] || null;
}

// Columns relevant for each view (subset to display in table mode)
// These map to what the DB view actually selects
export const VIEW_COLUMNS: Record<string, string[]> = {
  'vw_outstanding_transfers': [
    'title',
    'status_sample',
    'subject',
    'procedure_type',
    'event_start_date',
    'event_end_date',
    'transfer_origin',
    'transfer_destination',
    'transfer_method',
    'banking_entity',
    'performing_entity',
    'location_performed',
    'location_destination',
    'sample_received',
    'next_check_in_date',
    'outcome_notes',
    'created_at',
  ],
  'vw_sampling_patient': [
    'title',
    'status_sample',
    'status_patient',
    'subject',
    'procedure_type',
    'performed_on',
    'event_start_date',
    'event_end_date',
    'banking_entity',
    'performing_entity',
    'location_performed',
    'location_destination',
    'sample_received',
    'outcome_notes',
    'created_at',
  ],
  'vw_sample_status': [
    'title',
    'status_sample',
    'sample_type',
    'sample_subtype',
    'subject',
    'is_active',
    'priority',
    'procedure_type',
    'performed_on',
    'status_patient',
    'analysis_complete',
    'sample_received',
    'transfer_involved',
    'event_start_date',
    'event_end_date',
    'next_check_in_date',
    'outcome_notes',
    'derived_material',
    'place',
    'source_sample_label',
    'banking_entity',
    'performing_entity',
    'location_performed',
    'location_destination',
    'transfer_origin',
    'transfer_destination',
    'transfer_method',
    'structured_data',
    'created_at',
  ],
};

// View field mapping - maps database column names to display labels for timeline views
export const VIEW_FIELD_LABELS: Record<string, string> = {
  'title': 'Name',
  'is_active': 'Active?',
  'banking_entity': 'Banking Entity',
  'banking_entity_id': 'Banking Entity',
  'blocking': 'Blocking',
  'blocked_by_sample_id': 'Blocked By',
  'source_sample_id': 'Source Sample',
  'source_sample_label': 'Source Sample',
  'event_start_date': 'Date',
  'event_end_date': 'End Date',
  'location_destination': 'Location – Destination',
  'location_destination_id': 'Location – Destination',
  'location_performed': 'Location – Where Performed',
  'location_performed_id': 'Location – Where Performed',
  'next_check_in_date': 'Next Check-in Date',
  'outcome_notes': 'Outcome / Notes',
  'subject': 'Patient',
  'subject_id': 'Patient',
  'performed_on': 'Performed On',
  'performing_entity': 'Performing Entity',
  'performing_entity_id': 'Performing Entity',
  'priority': 'Priority',
  'procedure_type': 'Procedure Type',
  'sample_received': 'Sample Received',
  'status_patient': 'Status – Patient',
  'status_sample': 'Status – Sample / Cells',
  'transfer_destination': 'Transfer Destination',
  'transfer_destination_id': 'Transfer Destination',
  'transfer_involved': 'Transfer Involved?',
  'transfer_origin': 'Transfer Origin',
  'transfer_origin_id': 'Transfer Origin',
  'analysis_complete': 'Analysis Completion',
  'sample_type': 'Sample Type',
  'sample_subtype': 'Sample Subtype',
  'created_at': 'Created',
  'derived_material': 'Derived Material',
  'place': 'Place',
  'transfer_method': 'Transfer Method',
  'structured_data': 'Extra Fields',
};

// Get the display label for a field
export function getFieldLabel(fieldName: string): string {
  return VIEW_FIELD_LABELS[fieldName] || fieldName
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
